import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import LinkOffOutlined from '@mui/icons-material/LinkOffOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import { FREE_CONCURRENT_LIMIT, PREMIUM_CONCURRENT_LIMIT } from '../../../../shared/types/access';
import { Reveal } from '../../design/motion';
import { GlassPanel, Kicker } from '../../design/surfaces';
import { color } from '../../design/tokens';
import { login } from '../../services/auth.service';
import type { LiveEndReason } from '../../services/live';

const FULL_RETRY_SECONDS = 20;

interface Copy {
    icon: JSX.Element;
    kicker: string;
    title: string;
    body: string;
}

function copyFor(reason: LiveEndReason): Copy {
    switch (reason.code) {
        case 'SIGN_IN_REQUIRED':
            return { icon: <LoginOutlined />, kicker: 'Sign in', title: 'Sign in to open this', body: 'Shared with specific people. Sign in with Persera to continue.' };
        case 'FORBIDDEN':
            return { icon: <LockOutlined />, kicker: 'Private', title: "You're not on the list", body: 'Ask the owner to add your email.' };
        case 'WORKSPACE_ONLY':
            return { icon: <BusinessOutlined />, kicker: 'Workspace', title: reason.message || 'Only members of its workspace can open this', body: "You're not a member. Ask a workspace admin to add your sign-in email." };
        case 'RESOURCE_FULL':
            return { icon: <GroupsOutlined />, kicker: 'Full', title: 'This space is full right now', body: `Free spaces hold ${FREE_CONCURRENT_LIMIT} people at once, Premium spaces ${PREMIUM_CONCURRENT_LIMIT}. You'll get in when someone leaves.` };
        case 'PREMIUM_REQUIRED':
            return { icon: <WorkspacePremiumOutlined />, kicker: 'Premium', title: 'This needs CoSparks Premium', body: reason.message };
        case 'LINK_RESET':
            return { icon: <LinkOffOutlined />, kicker: 'Link reset', title: 'This link was reset', body: 'Ask the owner for the new one.' };
        case 'ACCESS_REVOKED':
            return { icon: <LockOutlined />, kicker: 'Access removed', title: 'Your access was removed', body: 'The owner changed who can open it.' };
        case 'DELETED':
            return { icon: <LinkOffOutlined />, kicker: 'Deleted', title: 'This space was deleted', body: 'Its owner deleted it.' };
        case 'NOT_FOUND':
        default:
            return { icon: <LinkOffOutlined />, kicker: 'Not found', title: "This link doesn't lead anywhere", body: 'It may be reset, deleted or mistyped.' };
    }
}

/**
 * Full-screen explanation when a resource can't be opened (or stopped being available while open).
 * `onRetry` is offered for RESOURCE_FULL, with an automatic countdown.
 */
export function AccessGate({ reason, onRetry }: { reason: LiveEndReason; onRetry?: () => void }) {
    const copy = copyFor(reason);
    const full = reason.code === 'RESOURCE_FULL';
    const [seconds, setSeconds] = useState(FULL_RETRY_SECONDS);

    useEffect(() => {
        if (!full || !onRetry) return;
        setSeconds(FULL_RETRY_SECONDS);
        const t = setInterval(() => {
            setSeconds(s => {
                if (s <= 1) {
                    onRetry();
                    return FULL_RETRY_SECONDS;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [full, onRetry]);

    return (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 2 }}>
            <Reveal>
                <GlassPanel corners sx={{ maxWidth: 460, p: { xs: 3, sm: 4.5 }, textAlign: 'center' }}>
                    <Box sx={{
                        mx: 'auto', mb: 2.5, width: 56, height: 56, display: 'grid', placeItems: 'center',
                        borderRadius: '50%', color: color.spark, backgroundColor: color.sparkSoft,
                        boxShadow: `0 0 40px -6px ${color.spark}88`,
                        '& svg': { fontSize: 28 },
                    }}>
                        {copy.icon}
                    </Box>
                    <Kicker sx={{ justifyContent: 'center', mb: 1 }}>{copy.kicker}</Kicker>
                    <Typography variant="h4" sx={{ mb: 1.5, fontSize: { xs: 26, sm: 30 } }}>{copy.title}</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>{copy.body}</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
                        {reason.code === 'SIGN_IN_REQUIRED' && (
                            <Button variant="contained" onClick={() => login()}>Sign in</Button>
                        )}
                        {reason.code === 'PREMIUM_REQUIRED' && (
                            <Button variant="contained" component={RouterLink} to="/account#plans">See plans</Button>
                        )}
                        {full && onRetry && (
                            <Button variant="contained" onClick={onRetry}>Try now ({seconds}s)</Button>
                        )}
                        <Button variant="outlined" component={RouterLink} to="/">Go to CoSparks</Button>
                    </Stack>
                </GlassPanel>
            </Reveal>
        </Box>
    );
}
